/* eslint-disable @typescript-eslint/no-unused-vars */
import { CheckCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { ArrowBackIos, SmsFailed } from '@mui/icons-material';
import LockIcon from '@mui/icons-material/Lock';
import { Col, message, Row } from 'antd';
import cn from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Card from '~/components/Card/Card';
import { OrderSummaryItemComponent } from '~/containers/OrderSummaryCard/OrderSummaryItem.component';
// import Button from '~/components/Button/Button';
import Button from '~/components/Button/Button';
import Loader from '~/components/Loader/Loader';
import { Text, Title } from '~/components/Typo/Typo';
import { LoadingContext } from '~/contexts/LoaderContext';
import { GetPaymentResponse } from '~/entities/responses/getPaymentResponse';
import { GetTransactionResponse } from '~/entities/responses/getTransactionResponse';
import { DataTransacton } from '~/entities/schemas/dataTransaction';
import { GeneralException } from '~/exceptions/generalException';
import { ABANDONED_PAGE_ROUTE } from '~/routing/routes';
import { CryptoService } from '~/services/CryptoService';
import { PaymentService } from '~/services/PaymentService';
import { FontWeight } from '~/theme/constants';
import { DATE_FORMAT, formatDate } from '~/utils/date';
import { logger } from '~/utils/func/logger';
import { isTransactionNotFoundError } from '~/utils/paymentResultUtils';
import { formatNumber } from '~/utils/number';

import styles from './OrderConfirmationAltUI.module.scss';

interface OrderConfirmationAltUIComponentProps {
  selectedItem?: number;
  paymentInfos?: GetPaymentResponse;
  loading?: boolean;
  dataTransaction: DataTransacton | null;
  loadingDataTr: boolean;
}

export const OrderConfirmationAltUIComponent: React.FC<OrderConfirmationAltUIComponentProps> = ({
  loading,
  dataTransaction,
  loadingDataTr,
}) => {
  const { t, i18n } = useTranslation();
  const { push } = useHistory();

  const [transactionResponse, setTransactionResponse] = useState<GetTransactionResponse>();
  const [transaction, setTransaction] = useState<GetPaymentResponse>();
  const [gettingReceipt, setGettingReceipt] = useState<boolean>(false);
  const [loadingView, setLoadingView] = useState<boolean>(true);

  const { startLoading, stopLoading } = useContext(LoadingContext);

  const handleRefreshTransaction = useCallback(
    async (transactionRef: string) => {
      startLoading();
      try {
        await PaymentService.getTransactionDetails(transactionRef);
        // stopLoading();
        window.location.reload();
      } catch (error: unknown) {
        stopLoading();
        if (isTransactionNotFoundError(error)) {
          push(ABANDONED_PAGE_ROUTE());
          return;
        }
        const exception = (error as { response?: { data?: GeneralException } })?.response?.data as GeneralException;
        if (process.env.NODE_ENV === 'development') {
          logger.debug('exception', { prefix: 'OrderConfirmationAltUI', data: exception });
        }
        message.error(exception?.description || t('home:global:generalResponse'), 3);
      }
    },
    [push],
  );

  const handleGetTransaction = useCallback(() => {
    // setLoadingView(true);
    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(
      localStorage.getItem('transactionDetails') as string,
    ) as GetTransactionResponse;

    setTransactionResponse(decryptData);
    logger.debug('decryptData', { prefix: 'OrderConfirmationAltUI', data: decryptData });

    if (decryptData !== null) {
      setLoadingView(false);
    }
  }, []);

  const handleGetTransactionsInfos = useCallback(() => {
    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;
    // const getData = JSON.parse(localStorage.getItem('data') as string);
    setTransaction(decryptData);
  }, []);

  const handleRedirect = useCallback((url: string) => {
    clearData();
    window.location.replace(url);
  }, []);

  const clearData = useCallback(() => {
    localStorage.removeItem('data');
    localStorage.removeItem('transactionDetails');
    clearCardInfos();
  }, []);

  const handleDownloadReceipt = useCallback(async (transactionRef: string) => {
    setGettingReceipt(true);
    try {
      const res = await PaymentService.getTransactionReceipt(transactionRef);
      setGettingReceipt(false);
      if (res !== '') {
        window.open(res, '_blank');
      }
    } catch (error: any) {
      setGettingReceipt(false);
    }
  }, []);

  const clearCardInfos = useCallback(() => {
    localStorage.removeItem('cardInfos');
  }, []);

  // const beforeUnloadHandler = useCallback((e: BeforeUnloadEvent) => {
  //   const confirmationMessage = 'Êtes-vous sûr de vouloir quitter la page ?';
  //   e.returnValue = confirmationMessage;
  // }, []);

  // const unloadHandler = useCallback(() => {
  //   clearData();
  // }, []);

  useEffect(() => {
    clearCardInfos();
    handleGetTransaction();
    handleGetTransactionsInfos();
    // window.addEventListener('beforeunload', beforeUnloadHandler);
    // window.addEventListener('unload', unloadHandler);

    // return () => {
    //   window.removeEventListener('beforeunload', beforeUnloadHandler);
    //   window.removeEventListener('unload', unloadHandler);
    // };
  }, []);

  return (
    <div className={styles.wrapper}>
      {loadingView ? (
        <Row justify="center">
          <Loader />
        </Row>
      ) : (
        /**
         * <div className={styles.sub_header}>
                <div className={styles.sub_header_title}>{`${formatNumber(
                  Number(transactionResponse?.data.transaction.total_amount) ?? 0,
                  i18n.language ?? 'fr',
                )} ${transactionResponse?.data?.transaction.currency}`}</div>
                <div>
                  {t('successPage:amountLocalCurrency')}:{' '}
                  {`${formatNumber(
                    transactionResponse?.data?.transaction.local_total_amount ?? 0,
                    i18n.language ?? 'fr',
                  )} ${transactionResponse?.data?.transaction.local_currency}`}
                </div>
              </div>
         */
        <div className={styles.container}>
          <Row justify="space-between">
            <div>
              <Title level={2}>Total</Title>
            </div>
            <div>
              <Title level={2}>
                {`${formatNumber(
                  Number(transactionResponse?.data.transaction.total_amount) ?? 0,
                  i18n.language ?? 'fr',
                )} ${transactionResponse?.data?.transaction.currency}`}
              </Title>
            </div>
          </Row>
          <Row justify="space-between">
            <div className={styles.title_above_label}> </div>
            <div className={styles.title_above_value}>
              {' '}
              {`(${formatNumber(
                transactionResponse?.data?.transaction.local_total_amount ?? 0,
                i18n.language ?? 'fr',
              )} ${transactionResponse?.data?.transaction.local_currency})`}
            </div>
          </Row>
          <Card slightlyRounded className={styles.card_wrapper}>
            <div className={styles.title_area}>
              {transactionResponse?.data.transaction.status === 'ACCEPTED' ? (
                <>
                  <Text weight={FontWeight.SEMIBOLD} className={styles.title}>
                    {t('orderConfirmation:thankYou')}
                  </Text>
                  <div className={styles.subtitle}>{t('orderConfirmation:YourPaymentSuccessful')}</div>
                  {dataTransaction?.application?.merchant?.name.toString().includes('impact') ? (
                    <div className={styles.extra_title}>
                      {t('orderConfirmation:thisIsNotVisa').replace(/\\n/g, '\n')}
                    </div>
                  ) : (
                    <div></div>
                  )}
                  <br />
                  <CheckCircleFilled style={{ color: '#479955' }} />
                </>
              ) : transactionResponse?.data.transaction.status === 'PENDING' ? (
                <>
                  <SmsFailed style={{ color: '#f4e195' }} />
                  <Text weight={FontWeight.SEMIBOLD} className={styles.title}>
                    {t('orderConfirmation:pending')}
                  </Text>
                  <div>{t('orderConfirmation:yourPaymentProcessed')}</div>
                </>
              ) : (
                <>
                  <SmsFailed fontSize="large" style={{ color: '#ff0000' }} />
                  <Text weight={FontWeight.SEMIBOLD} className={styles.title}>
                    {t('orderConfirmation:sorry')}
                  </Text>
                  <div>{t('orderConfirmation:yourPaymentFailed')}</div>
                </>
              )}
            </div>
            {/* <HeaderSummarySectionComponent
              title={`${transactionResponse?.data.application.name ?? ''}`}
              subTitle={`${transactionResponse?.data.application.description ?? ''}`}
            >
              <div className={styles.sub_header}>
                <div className={styles.sub_header_title}>{`${formatNumber(
                  Number(transactionResponse?.data.transaction.total_amount) ?? 0,
                  i18n.language ?? 'fr',
                )} ${transactionResponse?.data?.transaction.currency}`}</div>
                <div>
                  {t('successPage:amountLocalCurrency')}:{' '}
                  {`${formatNumber(
                    transactionResponse?.data?.transaction.local_total_amount ?? 0,
                    i18n.language ?? 'fr',
                  )} ${transactionResponse?.data?.transaction.local_currency}`}
                </div>
              </div>
            </HeaderSummarySectionComponent> */}
            {/* <hr /> */}
            {/* <OrderSummaryItemComponent
              title={t('successPage:merchant')}
              value={`${transactionResponse?.data.application.merchant.name ?? ''}`}
            />
            <OrderSummaryItemComponent
              title={t('orderConfirmation:descriptionOfPurchase')}
              value={`${transactionResponse?.data.transaction.description ?? ''}`}
            /> */}
            {/* <OrderSummaryItemComponent
              title={t('orderConfirmation:transactionReference')}
              value={`${transaction?.data.transactionRef}`}
            /> */}
            <OrderSummaryItemComponent
              title={dataTransaction?.application?.merchant?.name.toString().includes('impact') ? 'VISA ID:' : 'ID'}
              value={`${transactionResponse?.data?.transaction.transaction_id}`}
            />
            <OrderSummaryItemComponent
              title={t('orderConfirmation:purchaseDate') + ' :'}
              value={`${
                transactionResponse?.data?.transaction.payment_date !== null
                  ? formatDate(
                      transactionResponse?.data?.transaction.payment_date as unknown as Date,
                      DATE_FORMAT.DATE_TIME,
                    )
                  : transactionResponse?.data?.paidTransaction?.paymentDate !== null
                  ? formatDate(
                      transactionResponse?.data?.paidTransaction?.paymentDate as unknown as Date,
                      DATE_FORMAT.DATE_TIME,
                    )
                  : ''
              }  ${
                transactionResponse?.data?.transaction.payment_date_offset
                  ? '(' + transactionResponse?.data?.transaction.payment_date_offset + ')'
                  : ''
              }`}
            />

            {transactionResponse?.data?.transaction.status === 'PAYMENT_ALREADY_PROCESSED' && (
              <OrderSummaryItemComponent
                title={t('orderConfirmation:externalTransactionId') + ' :'}
                value={`${transactionResponse?.data?.paidTransaction?.transaction_id}`}
              />
            )}

            {transactionResponse?.data?.transaction.status === 'PAYMENT_ALREADY_PROCESSED' && (
              <OrderSummaryItemComponent
                title={t('orderConfirmation:descriptionOfPurchase') + ' :'}
                value={t('orderConfirmation:alreadyPaid')}
              />
            )}
            {transactionResponse?.data?.transaction.failure_reason !== null &&
              transactionResponse?.data?.transaction.status === 'REFUSED' && (
                <OrderSummaryItemComponent
                  title={t('orderConfirmation:failureReason') + ' :'}
                  value={`${transactionResponse?.data?.transaction.failure_reason}`}
                />
              )}
            <hr />
            {/* <Text className={styles.back_text} onClick={() => handleRedirect(transaction?.data?.returnUrl ?? '')}>
              &lt;{'  '}
              {t('orderConfirmation:backToTheSite')}
            </Text> */}

            <Row justify="center">
              <Col lg={10} md={10} sm={24} xs={24}>
                {transactionResponse?.data.transaction.status === 'PENDING' ? (
                  <Button
                    onClick={() =>
                      handleRefreshTransaction(
                        (transaction?.data.transactionRef || transactionResponse.data.transaction.transactionRef) ?? '',
                      )
                    }
                    // handleDownloadReceipt(transaction?.data.transactionRef as string)
                    title={t('orderConfirmation:refresh')}
                    size="block"
                    color="success"
                    rounded
                    icon={<ReloadOutlined className="mr-2" />}
                    type="button"
                    className={cn(styles.submit_button)}
                    loading={gettingReceipt}
                  />
                ) : (
                  <div></div>
                )}
              </Col>
              <Col lg={2} md={2} sm={2} xs={2}></Col>
              <Col
                lg={transactionResponse?.data.transaction.status === 'PENDING' ? 10 : 24}
                md={transactionResponse?.data.transaction.status === 'PENDING' ? 10 : 24}
                sm={24}
                xs={24}
              >
                <Button
                  onClick={() =>
                    handleRedirect((transaction?.data?.returnUrl || transactionResponse?.data.returnUrl) ?? '')
                  }
                  // handleDownloadReceipt(transaction?.data.transactionRef as string)
                  title={t('orderConfirmation:backToTheSite')}
                  size="block"
                  rounded
                  icon={<ArrowBackIos className="mr-2" />}
                  type="button"
                  className={cn(styles.submit_button)}
                  loading={gettingReceipt}
                />
              </Col>
            </Row>
            <div className={styles.footer}>
              <LockIcon />
              {t('orderSummary:securedByDOS')}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
